import { Dispatch, SetStateAction } from 'react';

// types
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import type { ItemProps, ModifiedItem } from './hero-image-gdds.types';
import type { TrackClick } from 'utils/tracking/track';

// utils
import { decodingContent } from 'utils/decodingContent';
import { isEmpty } from 'utils/is-empty';
import { onButtonClick } from 'utils/button-click-handler';

function isVideoAvailable(item: ItemProps) {
  return (
    (!isEmpty(item.video) && !isEmpty(item.video.videoId) && item.heroType === 'bgvideo') ||
    item?.videoType === 'bgvideo'
  );
}

export function getVideoSlide(items: ItemProps[]): ItemProps | undefined {
  if (!items || isEmpty(items)) {
    throw new Error('No video available!');
  }

  return items.find(isVideoAvailable);
}

export function fallbackValue(text: string | undefined, fallback: string) {
  if (isEmpty(text)) {
    return fallback ?? '';
  }

  return text;
}

export function getModifiedItems(
  router: AppRouterInstance,
  {
    items,
    isMobile,
    hideScrollIndicator,
    track,
    trackClick,
    translate,
    setIframeShown,
    isSection = true,
    trackClickElement,
    isXY,
  }: {
    items: ItemProps[];
    isMobile: boolean;
    track: {
      trackEvent: (data: any) => void;
    };
    trackClick: TrackClick;
    translate: (key: string, fallbackOnPreview?: boolean) => string;
    setIframeShown: Dispatch<SetStateAction<string | null>>;
    hideScrollIndicator?: boolean;
    isSection?: boolean;
    trackClickElement?: string;
    isXY?: boolean;
  },
): ModifiedItem[] {
  const newItems = items.reduce((acc, item) => {
    const {
      link,
      pictureAlt,
      textColor,
      buttonColor,
      colorVariant,
      imageObjectMobile,
      imageObject,
      headline,
      subline,
      subheadline,
      emphasize,
      textbox,
      video,
      videoType = undefined,
      position = undefined,
    } = item;
    const imageObj = isMobile ? imageObjectMobile : imageObject;
    const boxLink = textbox?.link || link;
    const title = textbox?.headlineDom ?? textbox?.headline ?? headline;
    const newCur = {
      image: {
        src: imageObj.url,
        alt: decodingContent(pictureAlt),
        title: decodingContent(pictureAlt),
      },
      color:
        textbox?.color ||
        textColor ||
        buttonColor ||
        (colorVariant && colorVariant === 'light' ? 'white' : 'black'),
      button: boxLink && {
        text: decodingContent(boxLink?.text) ?? '',
        onClick: (e) => {
          trackClick({
            click_intent: 'cta',
            click_element: trackClickElement
              ? trackClickElement
              : isSection
                ? 'hero slider button - section'
                : 'hero slider button - header',
            click_url: boxLink?.target,
          });
          onButtonClick(e, boxLink, track, router, setIframeShown);
        },
      },
      title: isXY ? title : decodingContent(title, true),
      titlePreviewId: '#st_headline',
      subtitlePreviewId: '#st_subline',
      subtitle: decodingContent(textbox?.subline ?? subline ?? subheadline, true),
      emphasize,
      scrollIndicatorText: hideScrollIndicator ? '' : translate('group_scroll'),
      boxposition: textbox?.position || position,
      video,
      videoType,
      headline,
    };

    return [...acc, newCur];
  }, []);

  return newItems;
}
